import QRCodeStyling from 'qr-code-styling';

export interface GenerateProfileQRCodeOptions {
  userName: string;
  profileUrl: string;
  logoUrl?: string;
  qrLogoUrl?: string;
  roleText?: string;
  companyName?: string;
}

export async function generateProfileQRCode({
  userName,
  profileUrl,
  logoUrl,
  qrLogoUrl = '/icon.png',
  roleText = 'Instructor at',
  companyName = 'TopTopTime'
}: GenerateProfileQRCodeOptions): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  const dpr = window.devicePixelRatio || 1;
  const width = 280;
  const padding = 20;
  const qrSize = width - padding * 2; // fill available width (240px)
  // height = top padding + username advance + role line advance + QR + bottom padding
  const height = padding + 26 + 22 + qrSize + padding;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.scale(dpr, dpr);

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  let currentY = padding;

  ctx.fillStyle = '#1F2937';
  ctx.font = 'bold 20px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(userName, width / 2, currentY);
  currentY += 26;

  ctx.fillStyle = '#6B7280';
  ctx.font = '14px Inter, system-ui, sans-serif';
  const displayText = roleText ? `${roleText} ` : '';

  if (logoUrl) {
    try {
      const logo = await loadImage(logoUrl);
      const logoHeight = 16;
      const logoWidth = (logo.width / logo.height) * logoHeight;
      const textWidth = ctx.measureText(displayText).width;
      const totalWidth = textWidth + logoWidth + 4;
      const startX = (width - totalWidth) / 2;

      ctx.textAlign = 'left';
      ctx.fillText(displayText, startX, currentY);
      ctx.drawImage(logo, startX + textWidth + 4, currentY - logoHeight + 3, logoWidth, logoHeight);
      currentY += 22;
    } catch {
      ctx.textAlign = 'center';
      const fallbackText = roleText && companyName ? `${roleText} ${companyName}` : companyName || '';
      ctx.fillText(fallbackText, width / 2, currentY);
      currentY += 22;
    }
  } else {
    ctx.textAlign = 'center';
    const fallbackText = roleText && companyName ? `${roleText} ${companyName}` : companyName || '';
    ctx.fillText(fallbackText, width / 2, currentY);
    currentY += 22;
  }

  const qrCode = new QRCodeStyling({
    width: qrSize * dpr,
    height: qrSize * dpr,
    type: 'canvas',
    data: profileUrl,
    image: qrLogoUrl,
    margin: 0,
    qrOptions: {
      errorCorrectionLevel: 'H'
    },
    dotsOptions: {
      color: '#1F2937',
      type: 'rounded'
    },
    cornersSquareOptions: {
      type: 'extra-rounded',
      color: '#fb5014'
    },
    cornersDotOptions: {
      type: 'dot',
      color: '#fb5014'
    },
    backgroundOptions: {
      color: '#FFFFFF'
    },
    imageOptions: {
      crossOrigin: 'anonymous',
      margin: 8,
      imageSize: 0.3,
      hideBackgroundDots: true
    }
  });

  const blob = (await qrCode.getRawData('png')) as Blob;
  const qrDataUrl = await blobToDataUrl(blob);
  const qrImage = await loadImage(qrDataUrl);
  ctx.drawImage(qrImage, (width - qrSize) / 2, currentY, qrSize, qrSize);

  return canvas.toDataURL('image/png');
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function downloadQRCode(dataUrl: string, fileName = 'profile-qr-code.png'): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
